// buildSchedulesVm.js — monta o SchedulesVM (@version 1) a partir da massa crua da API.
//
// Função PURA: recebe dados, devolve o vm. Nenhum fetch, nenhum hook, nenhum estado. É aqui que
// mora a regra de negócio da tela — a View só lê o resultado.
//
// ────────────────────────────────────────────────────────────────────────────
// A REGRA QUE ESTE ARQUIVO IMPLEMENTA
// ────────────────────────────────────────────────────────────────────────────
// `operation.isOperating` responde "esta agenda consegue oferecer horário agora?" e espelha as
// condições do motor de horários (DynamicSlotAvailabilityService + slotCalculator.js). Ela NÃO é
// derivável do status: uma agenda `Active` sem turno nenhum não opera, e foi exatamente esse par
// contraditório — chip verde "Ativa" ao lado de "Nenhum turno configurado" — que motivou o
// redesenho. Medição em produção (23-24/09/2026): 59 agendas ativas sem turno, 21 com a vigência
// vencida, 5 com turnos que não atendem serviço nenhum.
//
// Mesma definição usada por `EntraNoIndice` (Consumer), `AgendaNoIndice.Sql` (Auditor) e
// `ScheduleDeleteWarningService.Agendavel` (API). Mudou lá, muda aqui.

import { formatDate, formatDateTime, formatTimeSpan } from '../../utils/formatters';

// ── textos didáticos da trilha ──────────────────────────────────────────────
// Ficam aqui, e não no mock: o mock é massa de exemplo que viaja para o design-package; estes
// são os textos REAIS que o usuário lê.
const WHY = {
  shift:
    'O turno é a faixa de horas em que você atende — por exemplo, de segunda a sexta, das 9h às 18h. ' +
    'É de dentro dele que saem os horários que o seu cliente vê na página.\n\n' +
    'Sem nenhum turno cadastrado, não existe de onde tirar horário: a sua página abre normalmente, ' +
    'mas não mostra nada para marcar.',
  shift_active:
    'Um turno desligado continua salvo, mas é ignorado na hora de montar os horários — como se não existisse.\n\n' +
    'Isso é útil para guardar um horário que você não está usando agora (a agenda de sábado no inverno, ' +
    'por exemplo). Mas se TODOS os turnos estiverem desligados, não sobra nenhum horário para oferecer.',
  shift_service:
    'Cada turno precisa dizer quais serviços podem ser marcados dentro dele. É isso que permite ter, ' +
    'por exemplo, avaliação só de manhã e procedimento longo só à tarde.\n\n' +
    'Um turno que não aponta para nenhum serviço não consegue gerar horário para serviço algum — ele ' +
    'fica no cadastro, mas nunca aparece para o cliente.',
  validity:
    'A agenda pode ter uma data limite para oferecer horários — útil para um mutirão, uma temporada ou ' +
    'um contrato com fim marcado.\n\n' +
    'Depois dessa data ela continua ativa e visível aqui para você, mas para de oferecer qualquer ' +
    'horário novo ao cliente. Se a intenção não era essa, basta estender ou remover a data.',
  capacity:
    'A capacidade diz quantas pessoas cabem no mesmo horário: 1 para atendimento individual, ou mais ' +
    'para turma (uma aula de pilates com 8 vagas, por exemplo).\n\n' +
    'Com capacidade zero nenhum horário fica disponível, porque todos nascem lotados.',
};

const LIFECYCLE = {
  active: 'Ativa',
  paused: 'Pausada',
  scheduled: 'Programada',
  ended: 'Encerrada',
  deleted: 'Excluída',
};

// O backend já entrega o status derivado (Active/Inactive/Scheduled/Expired/Deleted); aqui só
// traduzimos para o vocabulário do VM. Derivar de novo a partir das datas duplicaria a regra.
const STATUS_TO_LIFECYCLE = {
  active: 'active',
  inactive: 'paused',
  scheduled: 'scheduled',
  expired: 'ended',
  deleted: 'deleted',
};

const DIAS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DIAS_CURTOS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const plural = (n, um, muitos) => `${n} ${n === 1 ? um : muitos}`;

/** "Seg a Sex" quando a sequência é contínua; "Ter e Qui" / "Seg, Qua e Sex" nos demais casos. */
function rotuloDeDias(ativos) {
  const idx = ativos.map((v, i) => (v ? i : -1)).filter((i) => i >= 0);
  if (idx.length === 0) return 'Nenhum dia';
  if (idx.length === 7) return 'Todos os dias';
  const contiguo = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (contiguo && idx.length > 2) return `${DIAS_CURTOS[idx[0]]} a ${DIAS_CURTOS[idx[idx.length - 1]]}`;
  const nomes = idx.map((i) => DIAS_CURTOS[i]);
  if (nomes.length === 1) return nomes[0];
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
}

function montarTurno(shift) {
  const daysActive = DIAS.map((d) => !!shift[d]);
  const serviceNames = (shift.services || []).map((s) => s.serviceName).filter(Boolean);
  const semServico = serviceNames.length === 0;

  let warning = null;
  if (semServico) warning = 'Este turno não atende nenhum serviço, então não gera horários.';
  else if (!shift.isActive) warning = 'Turno desligado — não gera horários.';

  return {
    id: shift.id,
    daysLabel: rotuloDeDias(daysActive),
    daysActive,
    timeLabel: `${formatTimeSpan(shift.startTime)} - ${formatTimeSpan(shift.endTime)}`,
    isActive: !!shift.isActive,
    serviceNames,
    breakLabel: shift.breakBetweenAppointmentsMinutes > 0
      ? `${shift.breakBetweenAppointmentsMinutes} min entre atendimentos`
      : null,
    warning,
  };
}

function montarFatos(s) {
  const fatos = [];
  if (s.maxAttendees > 1) {
    fatos.push({
      id: 'capacity',
      label: `Turma · ${plural(s.maxAttendees, 'vaga por horário', 'vagas por horário')}`,
      help: 'Quantas pessoas cabem no mesmo horário.',
      isDefault: false,
    });
  }
  fatos.push({
    id: 'bookingWindow',
    label: `Aceita reservas até ${plural(s.maxBookingDaysAhead, 'dia', 'dias')} à frente`,
    help: 'Até quantos dias à frente o cliente pode reservar.',
    isDefault: s.maxBookingDaysAhead === 30,
  });
  fatos.push({
    id: 'daysShown',
    label: `Mostra ${plural(s.maxAvailableDaysToShow, 'dia', 'dias')} por vez`,
    help: 'Quantos dias de horário a página exibe de uma vez.',
    isDefault: s.maxAvailableDaysToShow === 7,
  });
  fatos.push({
    id: 'minimumAdvance',
    label: `Reserva com ${plural(s.minimumAdvanceBookingHours, 'hora', 'horas')} de antecedência`,
    help: 'Quanto tempo antes do horário o cliente ainda consegue reservar.',
    isDefault: s.minimumAdvanceBookingHours === 1,
  });
  if (s.startDate) {
    fatos.push({
      id: 'startDate',
      label: `Começa em ${formatDate(s.startDate)}`,
      help: 'Primeiro dia com horários oferecidos.',
      isDefault: false,
    });
  }
  fatos.push({
    id: 'endDate',
    label: s.endDate ? `Horários até ${formatDate(s.endDate)}` : 'Sem data limite',
    help: 'Último dia com horários oferecidos ao cliente.',
    isDefault: !s.endDate,
  });
  return fatos;
}

/**
 * O veredito. Cada item do checklist é uma condição real do motor — nenhum é decorativo.
 * `blocking` é true em todos porque qualquer um deles sozinho zera os horários.
 */
function montarOperacao(s, turnos, lifecycle) {
  const temTurno = turnos.length > 0;
  const temTurnoLigado = turnos.some((t) => t.isActive);
  const temTurnoComServico = turnos.some((t) => t.isActive && t.serviceNames.length > 0);
  const dentroDaVigencia = lifecycle !== 'ended';
  const capacidadeOk = (s.maxAttendees ?? 0) >= 1;

  const item = (id, label, done, actionLabel, actionId) =>
    ({ id, label, done, blocking: true, why: WHY[id], actionLabel: done ? null : actionLabel, actionId: done ? null : actionId });

  const checklist = [
    item('shift', 'Tem ao menos um turno', temTurno, 'Criar turno', 'addShift'),
    item('shift_active', 'Ao menos um turno ligado', temTurnoLigado, 'Ver turnos', 'editShift'),
    item('shift_service', 'Os turnos atendem algum serviço', temTurnoComServico, 'Vincular serviço', 'editShift'),
    item('validity', 'Dentro do período de funcionamento', dentroDaVigencia, 'Estender a data', 'edit'),
    item('capacity', 'Capacidade definida', capacidadeOk, 'Editar agenda', 'edit'),
  ];

  const doneCount = checklist.filter((i) => i.done).length;
  const pendentes = checklist.filter((i) => !i.done);
  const configuracaoOk = pendentes.length === 0;

  const isByChoice = lifecycle === 'paused' || lifecycle === 'scheduled';
  const isOperating = lifecycle === 'active' && configuracaoOk;

  // Escolha do dono NUNCA é pendência: pausar ou programar são decisões, não defeitos.
  if (isByChoice) {
    return {
      isOperating: false,
      isByChoice: true,
      status: lifecycle === 'scheduled' ? 'info' : 'neutral',
      headline: lifecycle === 'scheduled'
        ? `Começa a receber em ${formatDate(s.startDate)}`
        : 'Pausada por você',
      detail: lifecycle === 'scheduled'
        ? 'Está tudo configurado. Os horários aparecem para o cliente a partir dessa data.'
        : 'Ninguém consegue marcar nesta agenda enquanto ela estiver pausada. É só reativar quando quiser.',
      checklist,
      doneCount,
      totalCount: checklist.length,
      progressLabel: configuracaoOk
        ? (lifecycle === 'paused' ? 'Tudo pronto — só falta reativar' : 'Tudo pronto')
        : `${plural(pendentes.length, 'item pendente', 'itens pendentes')}`,
    };
  }

  if (isOperating) {
    return {
      isOperating: true,
      isByChoice: false,
      status: 'success',
      headline: 'Recebendo agendamentos',
      detail: null,
      checklist,
      doneCount,
      totalCount: checklist.length,
      progressLabel: 'Tudo pronto',
    };
  }

  // Não opera e não foi escolha: é pendência de configuração.
  const primeiro = pendentes[0];
  const detalhePorItem = {
    shift: `Falta criar um turno com os horários em que ${s.professionalName || 'o profissional'} atende.`,
    shift_active: 'Todos os turnos desta agenda estão desligados, então não sobra horário nenhum para oferecer.',
    shift_service: 'Os turnos desta agenda não atendem nenhum serviço, então não há o que marcar neles.',
    validity: 'A data limite desta agenda passou. Ela continua aqui, mas não oferece mais horários.',
    capacity: 'A capacidade desta agenda está zerada, então todos os horários nascem lotados.',
  };

  return {
    isOperating: false,
    isByChoice: false,
    status: lifecycle === 'ended' ? 'warning' : 'error',
    headline: lifecycle === 'ended'
      ? `Parou de receber agendamentos em ${formatDate(s.endDate)}`
      : 'Não está recebendo agendamentos',
    detail: primeiro ? detalhePorItem[primeiro.id] : null,
    checklist,
    doneCount,
    totalCount: checklist.length,
    progressLabel: `${plural(pendentes.length, 'item', 'itens')} ${pendentes.length === 1 ? 'falta' : 'faltam'} para ${lifecycle === 'ended' ? 'voltar a' : 'começar a'} receber`,
  };
}

/**
 * @v2 — os rótulos que saíram das métricas agregadas na API.
 *
 * `pagesLabel` é o que dá TAMANHO ao problema: "não está recebendo agendamentos" é abstrato;
 * "e 2 páginas suas estão no ar agora sem conseguir marcar" não é. Null quando nenhuma página
 * publicada depende da agenda — aí o defeito é só interno e não merece o mesmo peso.
 */
function montarRotulosDeOperacao(s, lifecycle, canWrite) {
  const paginas = s.publishedPagesCount ?? 0;
  const futuros = s.futureAppointmentsCount ?? 0;

  const pagesLabel = paginas > 0
    ? `Alimenta ${plural(paginas, 'página sua', 'páginas suas')}`
    : null;

  let activityLabel = null;
  if (futuros > 0) {
    const marcados = plural(futuros, 'agendamento marcado', 'agendamentos marcados');
    activityLabel = s.nextAppointmentAt
      ? `${marcados} · próximo em ${formatDateTime(s.nextAppointmentAt)}`
      : marcados;
  }

  const autoConfirmLabel = s.autoConfirmAppointments
    ? 'Confirma automaticamente'
    : 'Você confirma cada agendamento';

  // Só faz sentido ligar/desligar o que está vivo e é seu. Programada e encerrada dependem de
  // data, não do interruptor — mostrar "Pausar" nelas prometeria um efeito que não acontece.
  const toggleLabel = !canWrite || lifecycle === 'deleted' ? null
    : lifecycle === 'paused' ? 'Reativar'
    : lifecycle === 'active' ? 'Pausar'
    : null;

  return { pagesLabel, activityLabel, autoConfirmLabel, toggleLabel };
}

function montarCard(s, shiftsDaAgenda) {
  const lifecycle = STATUS_TO_LIFECYCLE[String(s.status || 'Active').toLowerCase()] || 'active';
  const turnos = (shiftsDaAgenda || []).map(montarTurno);
  const canWrite = s.canWrite ?? true;

  return {
    ...montarRotulosDeOperacao(s, lifecycle, canWrite),
    id: s.id,
    name: s.name,
    description: s.description || null,
    professionalName: s.professionalName || 'Profissional não identificado',
    placeName: s.placeName || 'Local não identificado',
    lifecycle,
    lifecycleLabel: LIFECYCLE[lifecycle],
    operation: montarOperacao(s, turnos, lifecycle),
    facts: montarFatos(s),
    shifts: turnos,
    shiftsLabel: turnos.length === 0 ? 'Nenhum turno' : plural(turnos.length, 'turno', 'turnos'),
    isShared: !!s.isShared,
    canWrite,
    readOnlyReason: canWrite ? null : 'Você tem acesso somente de leitura a esta agenda.',
    canBook: lifecycle === 'active' && turnos.length > 0,
  };
}

// Locais (placesVmBuilder) compõe o "recebe agendamento?" de cada local a partir deste mesmo
// veredito de agenda — uma regra só, nunca uma cópia dela.
export { montarCard as buildScheduleCard, rotuloDeDias as daysLabel };

function montarResumo(cards) {
  const operating = cards.filter((c) => c.operation.isOperating).length;
  const byChoice = cards.filter((c) => c.operation.isByChoice).length;
  const withIssues = cards.filter((c) => !c.operation.isOperating && !c.operation.isByChoice).length;
  const total = cards.length;

  let headline;
  let status;
  if (total === 0) {
    headline = '';
    status = 'neutral';
  } else if (operating === 0) {
    headline = `Nenhuma das suas ${plural(total, 'agenda está', 'agendas está')} recebendo agendamento`;
    status = 'error';
  } else if (withIssues > 0) {
    headline = `${operating} de ${plural(total, 'agenda', 'agendas')} recebendo agendamento`;
    status = 'warning';
  } else {
    headline = `${operating} de ${plural(total, 'agenda', 'agendas')} recebendo agendamento`;
    status = 'success';
  }

  // O callout é para UMA situação e só ela: nada operando. É o caso que deixou 58 páginas no ar
  // sem conseguir agendar, por até dois meses, sem ninguém saber.
  const callout = total > 0 && operating === 0
    ? 'Sua página continua no ar, mas ninguém consegue marcar horário com você.'
    : null;

  return { operating, withIssues, byChoice, headline, callout, status };
}

/**
 * @param {Object} args
 * @param {Array}  args.schedules           lista crua de ScheduleForListDto
 * @param {Object} args.shiftsByScheduleId  { [scheduleId]: ScheduleShiftDto[] }
 * @param {Array}  args.professionals       para as opções do filtro
 * @param {Array}  args.places              para as opções do filtro
 * @param {Object} args.filters             { searchText, professional, place, status }
 * @param {boolean} args.loading
 * @param {string|null} args.error
 * @param {boolean} args.hasPlaces          false ⇒ conta sem local (muda o CTA do vazio)
 */
export function buildSchedulesVm({
  schedules = [],
  shiftsByScheduleId = {},
  professionals = [],
  places = [],
  filters = {},
  loading = false,
  error = null,
  hasPlaces = true,
}) {
  const { searchText = '', professional = '', place = '', status = '' } = filters;

  const base = {
    title: 'Agendas',
    subtitle: 'Quando e onde cada profissional atende',
    actionLabel: 'Nova agenda',
    filters: {
      searchText,
      professional,
      professionalOptions: [
        { value: '', label: 'Todos os profissionais' },
        ...professionals.map((p) => ({ value: p.id, label: p.name })),
      ],
      place,
      placeOptions: [
        { value: '', label: 'Todos os locais' },
        ...places.map((p) => ({ value: p.id, label: p.name })),
      ],
      status,
      statusOptions: [
        { value: '', label: 'Todas' },
        { value: 'active', label: 'Ativas' },
        { value: 'inactive', label: 'Pausadas' },
        { value: 'scheduled', label: 'Programadas' },
        { value: 'expired', label: 'Encerradas' },
        { value: 'deleted', label: 'Excluídas' },
      ],
      isFiltered: !!(searchText || professional || place || status),
      canClear: !!(searchText || professional || place || status),
    },
  };

  // Erro tem precedência sobre tudo: falhou, mostra a falha. Sem isso, um erro que chegasse
  // junto com a lista ficaria escondido atrás do skeleton da guarda abaixo, para sempre.
  if (error) {
    return {
      ...base,
      viewState: 'error',
      errorMessage: typeof error === 'string' ? error : 'Não foi possível carregar suas agendas. Verifique sua conexão e tente de novo.',
      summary: montarResumo([]),
      groups: [],
      emptyState: null,
    };
  }

  // ── A GUARDA CONTRA O FALSO ALARME ────────────────────────────────────────
  // Agendas e turnos vêm de chamadas SEPARADAS: a lista chega primeiro, os turnos logo depois.
  // Na janela entre as duas, toda agenda parece não ter turno — e a tela chegava a estampar
  // "Sua página continua no ar, mas ninguém consegue marcar horário com você" para quem estava
  // com tudo funcionando, só para se desmentir meio segundo depois.
  //
  // A distinção que resolve é entre AUSENTE e VAZIO:
  //   shiftsByScheduleId[id] === undefined → ainda não carregou      ⇒ seguir em 'loading'
  //   shiftsByScheduleId[id] === []        → carregou e não tem turno ⇒ pendência REAL (59 casos em PRD)
  //
  // Vale para qualquer chamador; o container também segura o `loading`, mas um acusa o outro se
  // um dia divergirem. Acusar alguém de estar quebrado sem ter os dados é pior do que demorar.
  const turnosPendentes = schedules.length > 0
    && schedules.some((s) => shiftsByScheduleId[s.id] === undefined);

  if (loading || turnosPendentes) {
    return { ...base, viewState: 'loading', errorMessage: null, summary: montarResumo([]), groups: [], emptyState: null };
  }

  // Vivas = tudo que não está excluído. É o universo que decide "conta sem agenda" — um filtro
  // de status não pode fazer a tela dizer que o usuário nunca criou agenda nenhuma.
  const vivas = schedules.filter((s) => String(s.status || 'Active').toLowerCase() !== 'deleted');

  const filtradas = schedules.filter((s) => {
    const busca = !searchText
      || s.name?.toLowerCase().includes(searchText.toLowerCase())
      || s.description?.toLowerCase().includes(searchText.toLowerCase());
    const porProfissional = !professional || s.professionalId === professional;
    const porLocal = !place || s.placeId === place;
    const porStatus = status
      ? String(s.status || '').toLowerCase() === status.toLowerCase()
      : String(s.status || 'Active').toLowerCase() !== 'deleted';
    return busca && porProfissional && porLocal && porStatus;
  });

  const cards = filtradas.map((s) => montarCard(s, shiftsByScheduleId[s.id]));
  // O resumo fala da CONTA, não do recorte: filtrar não pode mudar o veredito geral.
  const cardsDaConta = vivas.map((s) => montarCard(s, shiftsByScheduleId[s.id]));
  const summary = montarResumo(cardsDaConta);

  if (vivas.length === 0) {
    return {
      ...base,
      viewState: 'empty',
      errorMessage: null,
      summary,
      groups: [],
      emptyState: {
        kind: hasPlaces ? 'no_schedule' : 'no_place',
        title: 'Você ainda não tem agenda',
        message: hasPlaces
          ? 'Sem agenda, ninguém consegue marcar horário na sua página.'
          : 'Sem agenda, ninguém consegue marcar horário na sua página. O assistente cria tudo de uma vez: local, serviço, profissional e a agenda.',
        actionLabel: hasPlaces ? 'Criar minha agenda' : 'Configurar agora',
        actionId: hasPlaces ? 'create' : 'setup',
      },
    };
  }

  if (cards.length === 0) {
    return {
      ...base,
      viewState: 'ready',
      errorMessage: null,
      summary,
      groups: [],
      emptyState: {
        kind: 'filtered',
        title: 'Nenhuma agenda encontrada',
        message: searchText
          ? `Nenhuma agenda corresponde a "${searchText}". Tente outro termo ou limpe os filtros.`
          : 'Nenhuma agenda corresponde aos filtros aplicados.',
        actionLabel: 'Limpar filtros',
        actionId: 'clearFilters',
      },
    };
  }

  const proprias = cards.filter((c) => !c.isShared);
  const compartilhadas = cards.filter((c) => c.isShared);
  const groups = [];
  if (proprias.length > 0) groups.push({ id: 'own', title: 'Minhas agendas', subtitle: null, items: proprias });
  if (compartilhadas.length > 0) {
    groups.push({
      id: 'shared',
      title: 'Compartilhadas comigo',
      subtitle: 'Agendas de outros donos a que você tem acesso',
      items: compartilhadas,
    });
  }

  return { ...base, viewState: 'ready', errorMessage: null, summary, groups, emptyState: null };
}
